import {Typography} from '@mui/material';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {UpdateScope} from '../../../../backend/interfaces/care-service';
import {translations} from '../../../../i18n/translations';
import DeleteResourceConfirmationModal from '../../../delete-resource-confirmation-modal';
import {CompactModal} from '../../../modal/compact-modal';
import {useModalBtns} from '../../../modal/use-modal-btns';
import {CareServicesRepetitionsModal} from './care-services-repetitions-modal';

export type ConfirmationType = 'repetition' | 'single' | null;

// ---------------------------------------------
// ---------------- EDIT MODAL ----------------
// ---------------------------------------------
export function CareServicesEditConfirmationModal(props: {
  confirmationType: ConfirmationType;
  onCancel: () => void;
  onConfirm: (option: UpdateScope) => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);

  // If recurrent, show repetition options modal
  if (props.confirmationType === 'repetition') {
    return (
      <CareServicesRepetitionsModal
        mode="edit"
        loading={loading}
        onCancel={props.onCancel}
        onConfirm={async (option) => {
          try {
            setLoading(true);
            await props.onConfirm(option);
          } finally {
            setLoading(false);
          }
        }}
      />
    );
  }

  // If single, show single edit confirmation modal
  if (props.confirmationType === 'single') {
    return (
      <SaveConfirmationModal
        loading={loading}
        onCancel={props.onCancel}
        onConfirm={async () => {
          try {
            setLoading(true);
            await props.onConfirm('only-this-instance');
          } finally {
            setLoading(false);
          }
        }}
      />
    );
  }

  return null;
}

// -----------------------------------------------
// ---------------- DELETE MODAL ----------------
// -----------------------------------------------
export function CareServicesDeleteConfirmationModal(props: {
  confirmationType: ConfirmationType;
  onCancel: () => void;
  onConfirm: (option: UpdateScope) => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);
  const {t} = useTranslation();

  // If recurrent, show repetition options modal
  if (props.confirmationType === 'repetition') {
    return (
      <CareServicesRepetitionsModal
        mode="delete"
        loading={loading}
        onCancel={props.onCancel}
        onConfirm={async (option) => {
          try {
            setLoading(true);
            await props.onConfirm(option);
          } finally {
            setLoading(false);
          }
        }}
      />
    );
  }

  // If single, show single delete confirmation modal
  if (props.confirmationType === 'single') {
    return (
      <DeleteResourceConfirmationModal
        PaperProps={{sx: {maxWidth: '500px'}}}
        open={true}
        title={t(translations.care_services.modals.delete.title)}
        description={t(translations.care_services.modals.delete.description)}
        cannotBeUndoneMessage={t(translations.care_services.modals.delete.cannot_be_undone)}
        isDeleting={loading}
        onCancel={props.onCancel}
        onConfirm={async () => {
          try {
            setLoading(true);
            await props.onConfirm('only-this-instance');
          } finally {
            setLoading(false);
          }
        }}
      />
    );
  }

  return null;
}

// ----------------------------------------------------------------------------
// ----------------------------------------------------------------------------

function SaveConfirmationModal(props: {
  onCancel: () => void;
  onConfirm: () => void;
  loading?: boolean;
}) {
  const {t} = useTranslation();

  const actionBtns = useModalBtns({
    left: {
      type: 'cancel',
      disabled: props.loading,
      onClick: props.onCancel,
    },
    right: {
      type: 'save',
      loading: props.loading,
      onClick: () => {
        return props.onConfirm();
      },
    },
  });

  return (
    <CompactModal
      open
      title={t(translations.care_services.modals.edit_confirmation.title)}
      actions={actionBtns}
      onClose={props.onCancel}
      PaperProps={{sx: {maxWidth: '500px'}}}
      className="care-service-repetition-modal"
    >
      <Typography component="p" sx={{m: 2, mb: 0}} fontSize="14px" textAlign="center">
        {t(translations.care_services.modals.edit_confirmation.subtitle)}
      </Typography>
    </CompactModal>
  );
}
